import base64 from 'base-64';
import { type ChangeEvent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAccessibleShops } from 'shared/hooks/useAccessibleShops';
import { PageLoading } from 'shared/components/utils/PageLoading';
import type { Dispatch } from 'redux';
import { to } from 'routing/to';
import { LocalStorageKey } from 'shared/types';
import { localStorage } from 'shared/utils';

import UpButton from '../../components/up-button';
import UpContainer from '../../components/UpContainer/index';
import UpH3 from '../../components/up-h3/index';
import UpInput from '../../components/up-input';
import UpModalWindow from '../../components/up-modal-window';

import { createNewShop } from '../Admin/StoreAdministration/actions';
import { getShopList } from '../../actions/actions';
import { resetDashboard, fetchAllData } from '../LiveDashboard/actions';
import { getCookieExpirationDate, setCookie } from '../../utils/cookies';
import type { RootReduxState } from '../../utils/types';

import Footer from './Footer';

import { Row, Wrapper } from './styles';
import { selectCurrShop } from '../App/selectors';
import { selectShops } from './selectors';

interface TokenPayload {
  id: number;
}

const CreateNewShop = () => {
  const [createShop, setCreateShop] = useState(false);
  const [shopCreated, setShopCreated] = useState(false);
  const [shopName, setShopName] = useState('');

  const dispatch = useDispatch<Dispatch>();
  const intl = useIntl();

  const { shopsCount, isShopsReceived, isCreatingNewShop } =
    useSelector(selectShops);
  const isDashboardResetted = useSelector<RootReduxState, boolean>(
    (state) =>
      state.getIn([
        'customerBackendDashboard',
        'liveDashboard',
        'resetDashboard',
      ]) as boolean,
  );

  useEffect(openWhenNoShopsAreConfigured, [
    isShopsReceived,
    shopsCount,
    shopCreated,
  ]);
  useEffect(finishShopCreation, [
    createShop,
    isCreatingNewShop,
    isDashboardResetted,
    dispatch,
  ]);

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setShopName(evt.target.value);
  };

  if (shopCreated) return <CreatedShopSettings />;

  return (
    createShop && (
      <UpModalWindow
        title={
          <FormattedMessage
            id="containers.onboarding.createnewshop.title"
            defaultMessage="Welcome"
          />
        }
        closeable={false}
        languageSwitch
        height="auto"
      >
        <Wrapper>
          <UpContainer>
            <UpH3>
              <FormattedMessage
                id="containers.onboarding.createnewshop.header"
                defaultMessage="There is no registered shop for you yet."
              />
            </UpH3>
            <br />
            <FormattedMessage
              id="containers.onboarding.createnewshop.maintext"
              defaultMessage={`Please provide us with the name of your shop for the creation of a individual configuration. {breakingLine} Click the "Register Shop" button to proceed.`}
              values={{
                breakingLine: <br />,
              }}
            />
            <Row margin="20px 0 30px 0">
              <UpInput
                value={shopName}
                placeholder={intl.formatMessage({
                  id: 'containers.onboarding.createnewshop.placeholder',
                  defaultMessage: 'Your shop name',
                })}
                name="shopName"
                onChange={handleChange}
                margin="10px 0 0 0"
              />
              <UpButton
                disabled={!shopName}
                margin="10px 0 0 0"
                value={
                  <FormattedMessage
                    id="containers.onboarding.createnewshop.button"
                    defaultMessage="Register shop"
                  />
                }
                type="button"
                onClick={() => dispatch(createNewShop(shopName))}
                loading={isCreatingNewShop}
              />
            </Row>
            <Footer />
          </UpContainer>
        </Wrapper>
      </UpModalWindow>
    )
  );

  function openWhenNoShopsAreConfigured() {
    if (!shopCreated && isShopsReceived && shopsCount === 0)
      setCreateShop(true);
  }

  function finishShopCreation() {
    if (!createShop) return;
    const token = localStorage.getItem(LocalStorageKey.TOKEN);
    const { id }: TokenPayload = JSON.parse(
      base64.decode(token!.split('.')[1]),
    );
    if (isCreatingNewShop === false) dispatch(resetDashboard(id));
    if (!isDashboardResetted) return;
    dispatch(fetchAllData(id));
    dispatch(getShopList());
    setCreateShop(false);
    setShopCreated(true);
  }
};

const CreatedShopSettings = () => {
  const { t } = useTranslation();
  const {
    data: shops,
    isLoading,
    isFetching,
    isPaused,
    isError,
    refetch,
  } = useAccessibleShops({ refetchOnMount: 'always' });
  const shop = shops?.[0];

  if (isLoading || isFetching || isPaused) return <PageLoading />;
  if (isError || !shop) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => void refetch()}>
            {t('shopContext.retry')}
          </Button>
        }
      >
        {t(isError ? 'global.error.serverUnavailable' : 'shopContext.noShops')}
      </Alert>
    );
  }
  return <Navigate to={to.algorithm(shop.shopId)} replace />;
};

export default CreateNewShop;

export const SelectedShopCookie = () => {
  const { selectedShop } = useSelector(selectCurrShop);
  const { isShopsReceived, shopsCount } = useSelector(selectShops);
  useEffect(updateSelectedShopCookie, [
    selectedShop,
    isShopsReceived,
    shopsCount,
  ]);
  return null;

  function updateSelectedShopCookie() {
    if (!isShopsReceived || shopsCount === 0 || !selectedShop) return;
    setCookie('upcuid', selectedShop, {
      domain: '.uptain.de',
      expires: getCookieExpirationDate(new Date(), { minutes: 30 }),
    });
  }
};
